/**
 * SitePick front — お気に入り / 比較の内部集計への同期（docs/06 §9）
 *
 * localStorage の全体像（favorites / compare の商品 ID 配列）を
 * `POST /wp-json/sitepick/v1/engagement/sync` へ送る。差分計算はサーバ。
 * 送るのは「ランダムな client id + 商品 ID」だけ（氏名 / メール / 閲覧履歴は送らない）。
 *
 * - このタブでの追加 / 解除のあと（debounce）
 * - ページ表示時、前回の同期から 24 時間以上たっていたら（既存の localStorage を初回に取り込む）
 * - ページを離れるとき、未送信があれば `keepalive` で送る
 *
 * 失敗しても UI は何も変えない（次の変更 / 次回表示で揃う）。運営（SITEPICK.staff）は送らない。
 */

import { favoritesStore } from "./favorites-store.js";
import { compareStore } from "./compare-store.js";
import { readRaw, writeRaw } from "./storage.js";

const CLIENT_KEY = "sitepick_client_id";
const SYNCED_KEY = "sitepick_engagement_synced_at";
const DEBOUNCE_MS = 1200;
const RESYNC_MS = 24 * 60 * 60 * 1000;
const INITIAL_DELAY_MS = 2500;

/**
 * @returns {string} 32 hex のランダム ID（生成できない環境は空文字 = 同期しない）
 */
function clientId() {
  const existing = readRaw(CLIENT_KEY);
  if (existing && /^[a-f0-9]{32}$/.test(existing)) {
    return existing;
  }
  let id = "";
  try {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    id = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return "";
  }
  // 永続化できない環境（プライベートブラウズ等）では ID が毎回変わり集計を汚すので送らない。
  return writeRaw(CLIENT_KEY, id) ? id : "";
}

/**
 * @returns {string}
 */
function restBase() {
  const cfg = window.SITEPICK || {};
  const base = typeof cfg.restUrl === "string" && cfg.restUrl ? cfg.restUrl : "/wp-json/sitepick/v1/";
  return base.endsWith("/") ? base : `${base}/`;
}

export function initEngagementSync() {
  const cfg = window.SITEPICK || {};
  // wp_localize_script は真偽値を "1" / "" にする。
  if (String(cfg.engagement || "") !== "1" || String(cfg.staff || "") === "1") {
    return;
  }
  if (typeof window.fetch !== "function") {
    return;
  }

  const client = clientId();
  if (!client) {
    return;
  }

  let timer = 0;
  let dirty = false;
  let inFlight = false;

  /**
   * @param {boolean} unloading ページ離脱中（keepalive で投げっぱなし）
   */
  function send(unloading) {
    if (inFlight && !unloading) {
      dirty = true;
      return;
    }
    dirty = false;
    inFlight = true;
    const body = JSON.stringify({
      client,
      favorites: favoritesStore.get(),
      compare: compareStore.get(),
    });
    fetch(`${restBase()}engagement/sync`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body,
      credentials: "same-origin",
      keepalive: true,
    })
      .then((response) => {
        if (response.ok) {
          writeRaw(SYNCED_KEY, String(Date.now()));
        }
      })
      .catch(() => {
        /* 集計の失敗は UI に出さない */
      })
      .finally(() => {
        inFlight = false;
        if (dirty) {
          schedule();
        }
      });
  }

  function schedule() {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = 0;
      send(false);
    }, DEBOUNCE_MS);
  }

  function flush() {
    if (timer) {
      window.clearTimeout(timer);
      timer = 0;
      send(true);
    }
  }

  /**
   * @param {number[]} _ids
   * @param {{remote?: boolean}} [meta]
   */
  const onChange = (_ids, meta) => {
    if (meta && meta.remote) {
      return; // 別タブの操作。そのタブが送る。
    }
    schedule();
  };
  favoritesStore.subscribe(onChange);
  compareStore.subscribe(onChange);

  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush();
    }
  });

  const last = Number.parseInt(readRaw(SYNCED_KEY) || "0", 10);
  const stale = !Number.isFinite(last) || Date.now() - last > RESYNC_MS;
  const hasAny = favoritesStore.count() > 0 || compareStore.count() > 0;
  if (stale && (hasAny || last > 0)) {
    // 初回は「何か入っているとき」だけ。空のまま一度も同期していないブラウザは行を作らない。
    window.setTimeout(() => send(false), INITIAL_DELAY_MS);
  }
}
